/**
 * Feature flag client interface. Call sites depend on `flags` — not on
 * a specific provider (LaunchDarkly / PostHog / Statsig / GrowthBook).
 *
 * Default implementation reads from a static map seeded at boot. Replace
 * via `setFlagProvider()` once you wire up a real provider.
 */

export type FlagKey = string;

export interface FlagProvider {
  isEnabled(key: FlagKey, defaultValue?: boolean): boolean;
  get<T = unknown>(key: FlagKey, defaultValue: T): T;
  refresh(): Promise<void>;
}

class StaticFlagProvider implements FlagProvider {
  constructor(private values: Record<string, unknown> = {}) {}
  isEnabled(key: FlagKey, defaultValue = false): boolean {
    const v = this.values[key];
    return typeof v === 'boolean' ? v : defaultValue;
  }
  get<T>(key: FlagKey, defaultValue: T): T {
    return (this.values[key] as T) ?? defaultValue;
  }
  async refresh(): Promise<void> {
    // No-op for static provider.
  }
}

let active: FlagProvider = new StaticFlagProvider();

export const flags: FlagProvider = {
  isEnabled: (k, d) => active.isEnabled(k, d),
  get: (k, d) => active.get(k, d),
  refresh: () => active.refresh(),
};

export function setFlagProvider(provider: FlagProvider): void {
  active = provider;
}

/**
 * Typed accessors for app feature flags — keeps flag keys in one place.
 */
export const featureFlags = {
  /**
   * Show "N going" counts on venue / event cards. Off by default: until
   * there's real traffic a low count reads as anti-social-proof. Flip the
   * `going-count` flag on once volume justifies it.
   */
  goingCount: (): boolean => flags.isEnabled('going-count'),
};

/** Convenience for tests / boot: seed static values without writing a class. */
export function seedFlags(values: Record<string, unknown>): void {
  active = new StaticFlagProvider(values);
}
